import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from '@/components/ui/use-toast';

export interface EnrollmentToken {
  id: string;
  token_hash: string;
  tenant_id: string;
  device_name: string | null;
  expires_at: string;
  is_active: boolean;
  max_uses: number;
  uses_count: number;
  created_at: string;
  created_by: string;
}

export function useEnrollmentTokens() {
  const { profile } = useAuth();
  const [tokens, setTokens] = useState<EnrollmentToken[]>([]);
  const [loading, setLoading] = useState(false);

  const generateToken = async (deviceName?: string): Promise<string | null> => {
    if (!profile?.tenant_id) {
      toast({
        title: "Error",
        description: "No tenant ID found",
        variant: "destructive",
      });
      return null;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-enrollment-token', {
        body: {
          tenant_id: profile.tenant_id,
          device_name: deviceName,
          max_uses: 1,
          expires_in_hours: 24
        }
      });

      if (error) throw error;

      toast({
        title: "Token generated",
        description: "Copy the token now - it won't be shown again",
      });

      await fetchTokens();
      return data.token;
    } catch (error) {
      console.error('Error generating token:', error);
      toast({
        title: "Error",
        description: "Failed to generate enrollment token",
        variant: "destructive",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchTokens = async () => {
    if (!profile?.tenant_id) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('enrollment_tokens')
        .select('id, token_hash, tenant_id, device_name, expires_at, is_active, max_uses, uses_count, created_at, created_by')
        .eq('tenant_id', profile.tenant_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTokens(data || []);
    } catch (error) {
      console.error('Error fetching tokens:', error);
      toast({
        title: "Error",
        description: "Failed to load enrollment tokens",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const revokeToken = async (tokenId: string) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('enrollment_tokens')
        .update({ is_active: false })
        .eq('id', tokenId)
        .eq('tenant_id', profile?.tenant_id);

      if (error) throw error;

      toast({
        title: "Token revoked",
        description: "The enrollment token has been deactivated",
      });

      await fetchTokens();
    } catch (error) {
      console.error('Error revoking token:', error);
      toast({
        title: "Error",
        description: "Failed to revoke token",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    tokens,
    loading,
    generateToken,
    fetchTokens,
    revokeToken,
  };
}
